import { ServerClient } from 'common/utils/http/http-clients';
import {
  IAddressOccupant,
  IAdmitGuest,
  IAdmitVisitor,
  ICreateGuest,
  ICreateGuestLimitations,
  ICreateGuestResponse,
  ITurnbackGuest,
  ITurnbackVisitor,
  IUpdateGuest,
  IUpdateResidentAddressDataSharing,
} from './guest.service.types';
import { GuestValidity } from 'common/enums/guest-validity.enum';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { IServerErrorMessage } from 'common/utils/http/http-client.types';
import { notification } from 'common/utils/notification';
import configurationStore from 'common/stores/configuration/configuration.store';
import featureStore from 'common/stores/features/feature.store';
import { validityOptions } from 'common/constans/guest-validity-options.constant';

dayjs.extend(utc);

class GuestService {
  async createGuest(residentId: number, addressId: number, data: ICreateGuest): Promise<ICreateGuestResponse> {
    const res = await ServerClient.post<ICreateGuestResponse>(`/v2/residents/${residentId}/addresses/${addressId}/guests`, data);
    return res?.data;
  }

  async updateGuest(residentId: number, addressId: number, data: IUpdateGuest): Promise<IUpdateGuest> {
    const res = await ServerClient.patch<IUpdateGuest>(`/v2/residents/${residentId}/addresses/${addressId}/guests/${data.id}`, data);
    return res?.data;
  }

  async deleteGuest(residentId: number, addressId: number, guestId: number): Promise<any> {
    const res = await ServerClient.delete(`/residents/${residentId}/addresses/${addressId}/guests/${guestId}`);
    return res?.data;
  }

  async updateResidentAddressDataSharing(
    residentId: number,
    addressId: number,
    data: IUpdateResidentAddressDataSharing,
  ): Promise<IAddressOccupant> {
    const res = await ServerClient.patch<IAddressOccupant>(`/residents/${residentId}/addresses/${addressId}/data-sharing`, data);
    return res?.data;
  }

  async admitGuest(residentId: number, addressId: number, guestId: number, data: IAdmitGuest): Promise<void> {
    const res = await ServerClient.post<void>(`/residents/${residentId}/addresses/${addressId}/admittance/guests/${guestId}`, data);
    return res?.data;
  }

  async admitVisitor(residentId: number, addressId: number, data: IAdmitVisitor): Promise<void> {
    const res = await ServerClient.post<void>(`/residents/${residentId}/addresses/${addressId}/admittance/visitors`, data);
    return res?.data;
  }

  async admitResident(residentId: number, addressId: number, data: IAdmitGuest): Promise<void> {
    const res = await ServerClient.post<void>(`/residents/${residentId}/addresses/${addressId}/admittance`, data);
    return res?.data;
  }

  async turnbackVisitor(residentId: number, addressId: number, data: ITurnbackVisitor): Promise<void> {
    const res = await ServerClient.post<void>(`/residents/${residentId}/addresses/${addressId}/turn-back/visitors`, data);
    return res?.data;
  }

  async turnbackGuest(residentId: number, addressId: number, guestId: number, data: ITurnbackGuest): Promise<void> {
    const res = await ServerClient.post<void>(`/residents/${residentId}/addresses/${addressId}/turn-back/guests/${guestId}`, data);
    return res?.data;
  }

  async handleGuestsLimitationsErrors<T>(saveGuest: () => Promise<T>, onError: () => void): Promise<T> {
    try {
      const data = await saveGuest();
      return data;
    } catch (error: any) {
      const errorMassage: IServerErrorMessage = error?.response?.data;
      notification.destroy();
      await configurationStore.requestConfiguration();

      if (errorMassage?.errorCode === 'MaximumGuestValidityRangeExceeded' || errorMassage?.errorCode === 'MaximumDateAheadExceeded') {
        notification.error({
          message: `Community settings have been changed, please try again.`,
        });
        onError();
      }

      if (errorMassage?.errorCode === 'PermanentGuestLimitExceeded') {
        notification.error({
          message: `You cannot add more than ${configurationStore.configuration?.guestLimitations.permanentGuestsLimitation} permanent guests to your guest list. Either remove one permanent guest or add this guest with an expiration date.`,
        });
      }
      return Promise.reject(error);
    }
  }

  getDefaultValidityOptions = () => {
    return validityOptions;
  };

  getLimitedValidityOptions = () => {
    return validityOptions.filter((opt) => opt.value !== GuestValidity.Permanent);
  };

  isResidentTypeLimited = (ownershipType: string | null): boolean => {
    if (!ownershipType) return false;

    return (
      featureStore.isGuestLimitationsEnabled &&
      (configurationStore.configuration?.guestLimitations.ownershipTypesConfiguration?.some(
        (x) => x.type === ownershipType && x.isLimited,
      ) ??
        false)
    );
  };

  getLimitations = (ownershipType: string | null): ICreateGuestLimitations | null => {
    const isLimitted = guestService.isResidentTypeLimited(ownershipType);
    if (isLimitted) {
      const guestLimitations = configurationStore.configuration?.guestLimitations;
      if (guestLimitations) {
        const limitations: ICreateGuestLimitations = {
          maximumGuestValidityLimitation: guestLimitations.maximumGuestValidityLimitation,
          maximumStartDateAheadLimitation: guestLimitations.maximumStartDateAheadLimitation,
          isPermanentAllowed: guestLimitations.permanentGuestsLimitation > 0,
        };
        return limitations;
      }
    }
    return null;
  };
}

export const guestService = new GuestService();
export default guestService;
